import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { usePortalStore } from '@/stores/portal.store';
import {
  LayoutDashboard,
  FileText,
  CreditCard,
  Ticket,
  User,
  LogOut,
  Wifi,
  Activity,
  Menu,
  X,
  ArrowUpCircle,
} from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';

const navItems = [
  { label: 'Dashboard', href: '/portal', icon: LayoutDashboard },
  { label: 'My Connection', href: '/portal/connection', icon: Wifi },
  { label: 'Usage', href: '/portal/usage', icon: Activity },
  { label: 'Invoices', href: '/portal/invoices', icon: FileText },
  { label: 'Payments', href: '/portal/payments', icon: CreditCard },
  { label: 'Tickets', href: '/portal/tickets', icon: Ticket },
  { label: 'Upgrade', href: '/portal/upgrade', icon: ArrowUpCircle },
  { label: 'Profile', href: '/portal/profile', icon: User },
];

export function PortalLayout() {
  const { subscriber, logout } = usePortalStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/portal/login');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Top Bar */}
      <header className="sticky top-0 z-40 border-b bg-card/80 backdrop-blur-xl">
        <div className="mx-auto flex h-14 max-w-5xl items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="rounded-lg p-1.5 text-muted-foreground hover:bg-accent sm:hidden"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <Link to="/portal" className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
                <Wifi className="h-4 w-4 text-primary-foreground" />
              </div>
              <span className="font-semibold text-foreground hidden sm:inline">My Account</span>
            </Link>
          </div>

          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-foreground">
                {subscriber?.firstName} {subscriber?.lastName}
              </p>
              <p className="text-[11px] text-muted-foreground">{subscriber?.subscriberCode}</p>
            </div>
            <button
              onClick={handleLogout}
              className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              title="Logout"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Nav */}
      {mobileOpen && (
        <div className="fixed inset-0 z-30 bg-black/50 sm:hidden" onClick={() => setMobileOpen(false)}>
          <nav
            className="absolute left-0 top-14 w-64 border-r bg-card shadow-xl h-[calc(100vh-3.5rem)] p-4 space-y-1"
            onClick={(e) => e.stopPropagation()}
          >
            {navItems.map((item) => {
              const isActive = location.pathname === item.href ||
                (item.href !== '/portal' && location.pathname.startsWith(item.href));
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setMobileOpen(false)}
                  className={cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                  )}
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>
      )}

      {/* Desktop Nav + Content */}
      <div className="mx-auto max-w-5xl px-4 py-6">
        <nav className="mb-6 hidden sm:flex items-center gap-1 rounded-xl border bg-card p-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.href ||
              (item.href !== '/portal' && location.pathname.startsWith(item.href));
            return (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                )}
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <Outlet />
      </div>
    </div>
  );
}
